import React, { useEffect, lazy, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import SubscriptionGuard from './components/SubscriptionGuard';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './components/Layout';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App as CapacitorApp } from '@capacitor/app';
import LoadingScreen from './components/LoadingScreen';

// Lazy load pages for better initial load performance
const Home = lazy(() => import('./pages/Home'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Hospitals = lazy(() => import('./pages/Hospitals'));
const AddHospital = lazy(() => import('./pages/AddHospital'));
const EditHospital = lazy(() => import('./pages/EditHospital'));
const AddCategory = lazy(() => import('./pages/AddCategory'));
const Visits = lazy(() => import('./pages/Visits'));
const AddVisit = lazy(() => import('./pages/AddVisit'));
const EditVisit = lazy(() => import('./pages/EditVisit'));
const Reports = lazy(() => import('./pages/Reports'));
const PaymentDues = lazy(() => import('./pages/PaymentDues'));
const MonthlyPaymentDues = lazy(() => import('./pages/MonthlyPaymentDues'));
const Profile = lazy(() => import('./pages/Profile'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Payment = lazy(() => import('./pages/Payment'));
const Help = lazy(() => import('./pages/Help'));
const About = lazy(() => import('./pages/About'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Email verification gate:
  // If the user is authenticated but email is not verified, force them to profile.
  // (Skip this for the profile page itself to avoid redirect loops.)
  if (!user?.isEmailVerified && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace state={{ reason: 'email_verification_required' }} />;
  }

  // If subscription is required, wrap with subscription guard
  if (requireSubscription) {
    // Never require subscription gating for the email/profile flow itself.
    if (location.pathname === '/profile') return children;
    return <SubscriptionGuard>{children}</SubscriptionGuard>;
  }
  
  return children;
};

// Public Route component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) return children;
  return <Navigate to={user?.isEmailVerified ? "/dashboard" : "/profile"} />;
};

// Back Button Handler Component (needs to be inside Router)
function BackButtonHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleBackButton = async ({ canGoBack }) => {
      // First, check if chat widget wants to handle it
      const chatHandled = new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(false), 100);
        const handler = () => {
          clearTimeout(timeout);
          window.removeEventListener('meera:back-handled', handler);
          resolve(true);
        };
        window.addEventListener('meera:back-handled', handler, { once: true });
        
        // Dispatch event for chat widget
        const ev = new CustomEvent('meera:back');
        window.dispatchEvent(ev);
        
        // If chat doesn't handle it within 100ms, proceed with navigation
        setTimeout(() => {
          clearTimeout(timeout);
          window.removeEventListener('meera:back-handled', handler);
          resolve(false);
        }, 100);
      });

      const chatConsumed = await chatHandled;
      if (chatConsumed) {
        return; // Chat widget handled it
      }

      // Get current path
      const currentPath = location.pathname;
      
      // If user is not authenticated, allow default behavior (go to login or close)
      if (!isAuthenticated) {
        // If on login/register pages, allow app to close
        if (currentPath === '/login' || currentPath === '/register' || currentPath === '/verify-email') {
          if (window.confirm('Do you want to close the app?')) {
            CapacitorApp.exitApp();
          }
        }
        return;
      }
      
      // If not on dashboard, navigate to dashboard
      if (currentPath !== '/dashboard' && currentPath !== '/') {
        navigate('/dashboard');
        return;
      }

      // If on dashboard, ask for confirmation to close app
      if (currentPath === '/dashboard' || currentPath === '/') {
        if (window.confirm('Do you want to close the app?')) {
          CapacitorApp.exitApp();
        }
      }
    };

    const backListener = CapacitorApp.addListener('backButton', handleBackButton);

    return () => {
      if (backListener) backListener.remove();
    };
  }, [location.pathname, navigate, isAuthenticated]);

  return null;
}

// Inner component that has access to AuthContext
function AppContent() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isLoading: subscriptionLoading } = useSubscription();
  const { isPageLoading, loadingProgress, loadingMessage, setLoading } = useLoading();
  const [initialLoadingProgress, setInitialLoadingProgress] = useState(0);
  const [initialLoadingMessage, setInitialLoadingMessage] = useState('Initializing DrPay...');

  // Calculate initial loading progress based on different states
  useEffect(() => {
    let progress = 0;
    let message = 'Initializing DrPay...';

    if (authLoading) {
      progress = 30;
      message = 'Connecting to DrPay...';
    } else if (isAuthenticated && subscriptionLoading) {
      progress = 60;
      message = 'Checking subscription...';
    } else if (isAuthenticated && !subscriptionLoading) {
      progress = 90;
      message = 'Loading dashboard...';
    } else if (!authLoading && !isAuthenticated) {
      progress = 100;
      message = 'Ready';
    }

    setInitialLoadingProgress(progress);
    setInitialLoadingMessage(message);
  }, [authLoading, isAuthenticated, subscriptionLoading]);

  // Show loading screen if auth or subscription is loading (initial load)
  const [loadingComplete, setLoadingComplete] = useState(false);
  const showInitialLoading = (authLoading || (isAuthenticated && subscriptionLoading)) && !loadingComplete;
  
  // Show loading screen for page/data loading
  const showPageLoading = isPageLoading && !showInitialLoading;

  return (
    <>
      {showInitialLoading && (
        <LoadingScreen 
          progress={initialLoadingProgress} 
          message={initialLoadingMessage} 
          onComplete={() => setLoadingComplete(true)}
        />
      )}
      {showPageLoading && (
        <LoadingScreen 
          progress={loadingProgress} 
          message={loadingMessage} 
          onComplete={() => setLoading(false)}
        />
      )}
      {!showInitialLoading && <AppRoutes />}
      <ToastContainer position="top-right" autoClose={1000} hideProgressBar theme="colored" />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - No Auth Required */}
      <Route
        path="/"
        element={
          <Suspense fallback={<PageLoader />}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path="/home"
        element={
          <Suspense fallback={<PageLoader />}>
            <Home />
          </Suspense>
        }
      />
      <Route
        path="/privacy-policy"
        element={
          <Suspense fallback={<PageLoader />}>
            <PrivacyPolicy />
          </Suspense>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Suspense fallback={<PageLoader />}>
              <Register />
            </Suspense>
          </PublicRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <Suspense fallback={<PageLoader />}>
              <VerifyEmail />
            </Suspense>
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Dashboard />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospitals"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Hospitals />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospitals/add"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AddHospital />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospitals/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <EditHospital />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AddCategory />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Visits />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/add"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AddVisit />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <EditVisit />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Reports />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-dues"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <PaymentDues />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-dues/monthly"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <MonthlyPaymentDues />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Profile />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Subscription />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Payment />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <Help />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/about"
        element={
          <ProtectedRoute requireSubscription={false}>
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <About />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Default redirect - redirect authenticated users to dashboard */}
      <Route 
        path="/dashboard-redirect" 
        element={<Navigate to="/dashboard" />} 
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Ensure the webview does not underlap the status bar
      StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});
      // Optional styling for readability
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
      StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {});
      StatusBar.show().catch(() => {});

      // Add padding to body to account for status bar and navigation bar on Android
      const updateBodyPadding = () => {
        const safeAreaTop = getComputedStyle(document.documentElement)
          .getPropertyValue('env(safe-area-inset-top)') || '0px';
        const safeAreaBottom = getComputedStyle(document.documentElement)
          .getPropertyValue('env(safe-area-inset-bottom)') || '0px';
        document.body.style.paddingTop = `calc(${safeAreaTop} + 0px)`;
        document.body.style.paddingBottom = `calc(${safeAreaBottom} + 0px)`;
      };

      // Update on load and resize
      updateBodyPadding();
      window.addEventListener('resize', updateBodyPadding);

      // Permissions are now requested through the PermissionsOnboarding component
      // This ensures users understand why permissions are needed

      // Back button handling is now done in BackButtonHandler component inside Router

      // Stop voice recognition on app pause to avoid frozen UI from active mic sessions
      const pauseListener = CapacitorApp.addListener('pause', () => {
        const ev = new CustomEvent('meera:pause');
        window.dispatchEvent(ev);
      });
      const resumeListener = CapacitorApp.addListener('resume', () => {
        const ev = new CustomEvent('meera:resume');
        window.dispatchEvent(ev);
      });

      return () => {
        window.removeEventListener('resize', updateBodyPadding);
        if (pauseListener) pauseListener.remove();
        if (resumeListener) resumeListener.remove();
      };
    }
  }, []);

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <LoadingProvider>
            <BackButtonHandler />
            <AppContent />
          </LoadingProvider>
        </Router>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
